import React, { FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Topic } from "../../components/item-content-two/Topic";
import { DEF_PG_SIZE_DROP_DOWN_MAX, DEF_PG_SIZE_LIST_W_ADD, DEF_PG_SIZE_SUB } from '../../constants/maximum-page-size';
import { ListQuery } from "../../models/ListQuery";
import { MultiSelectOption } from "../../models/MultiSelectOption";
import { Name } from "../../models/Name";
import { PaginationModel } from "../../models/Pagination";
import { TopicResponse } from "../../models/services/TopicResponse";
import { showError } from "../../services/utilities/ServiceError";
import { TranslationKeys } from "../TranslationKeys";
import { useLocale } from "../localeContext";
import { PaginationContext } from "../table/PaginationContext";
import { TopicContext } from "./TopicContext";

interface TopicContextProviderModel {
    dataMethod: (query?: ListQuery) => Promise<TopicResponse>;
    itemId: string;
    topic: Topic;
    selectedItem?: MultiSelectOption;
}

export const TopicContextProvider: FC<TopicContextProviderModel> = ({ children, dataMethod, itemId, topic, selectedItem }) => {
    const [data, setData] = useState<Name[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [pagination, setPagination] = useState<PaginationModel>({
        maxPageSize: DEF_PG_SIZE_LIST_W_ADD,
        pageNumber: 1,
        totalCount: 0,
        totalPages: 1,
        sizes: [10, 50, 100],
        pages: [1]
    });
    const [query, setQuery] = useState<ListQuery>({
        filters: undefined,
        goToPage: pagination.pageNumber,
        pageSize: pagination.maxPageSize,
        searchTerm: { accessor: topic.accessor, translationKey: '' },
        sortBy: undefined
    });
    const { translate } = useLocale();

    useEffect(() => {
        refreshData(query);
    }, [itemId])

    // A change in selectedItem should not make an API call
    // but, it should update the UI. Hence, we are using
    // existing data.
    useEffect(() => {
        updateData(data);
    }, [selectedItem])

    const updateData = (data: Name[]) => {
        let updatedData = [...data];
        const isItemFound = updatedData.find(item => item.value === selectedItem?.value);
        if (selectedItem && !isItemFound) {
            updatedData = [selectedItem, ...updatedData];
        } else if (isItemFound && selectedItem?.label !== isItemFound.label) {
            updatedData = updatedData.map(item => {
                if (item.value === selectedItem?.value) {
                    item.label = selectedItem.label;
                }
                return item;
            })
        }
        setData(updatedData);
    }

    const refreshData = async (query: ListQuery) => {
        setIsLoading(true);
        try {
            const response: TopicResponse = await dataMethod(query);
            updateData(response.rows);
            setPagination({ ...pagination, ...response.pagination });
        }
        catch (error) {
            showError(error, toast.warn, translate.byKey(TranslationKeys.loadTopicFailed));
        }
        finally {
            setIsLoading(false);
        }
    }

    const onSearch = (searchValue: string) => {
        const updatedQuery = {
            ...query,
            goToPage:1, // Reset to first page on new search
            searchTerm:{
                accessor:topic.accessor,
                translationKey:'',
                value:searchValue
            }
        }
        setQuery(updatedQuery);
        refreshData(updatedQuery);
    }

    const updatePageNumber = (pageNumber: number) => {
        if (pageNumber > 0 && pageNumber <= pagination.totalPages) {
            const updatedQuery = {
                ...query,
                goToPage: pageNumber
            }
            setQuery(updatedQuery)
            refreshData(updatedQuery);
        }
    }

    const updatePageSize = (pageSize: number) => {
        if (pagination.sizes?.includes(pageSize)) {
            const updatedQuery = {
                ...query,
                goToPage:1, //Reset to first page when changing page size
                pageSize:pageSize
            };
            setQuery(updatedQuery);
            refreshData(updatedQuery);
        }
    }

    return <TopicContext.Provider value={{ data, isLoading, itemId, onSearch, topic, selectedItem  }}>
        <PaginationContext.Provider value={{ isInUse: true, pagination, updatePageNumber, updatePageSize }}>
            {children}
        </PaginationContext.Provider>
    </TopicContext.Provider>
}
